import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, VideoCamera, Warning, ArrowsOut, ArrowsIn } from "@phosphor-icons/react";

export default function MeetingRoomPage({ guest = false }) {
  const { meetingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inMeeting, setInMeeting] = useState(false);
  const [mode, setMode] = useState("embedded");
  const [guestName, setGuestName] = useState("");
  const jitsiRef = useRef(null);
  const apiRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    if (guest) {
      const name = new URLSearchParams(window.location.search).get("name") || "Guest";
      setGuestName(name);
      api.get(`/meetings/${meetingId}/guest?name=${encodeURIComponent(name)}`).then(r => {
        setMeeting(r.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      api.get(`/meetings/${meetingId}/room`).then(r => {
        setMeeting(r.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [meetingId, guest]);

  const initJitsi = (parentNode) => {
    if (!window.JitsiMeetExternalAPI || !parentNode) return;
    if (apiRef.current) {
      try { apiRef.current.dispose(); } catch (e) {}
    }
    const domain = "meet.jit.si";
    const jitsiApi = new window.JitsiMeetExternalAPI(domain, {
      roomName: meeting.room_name,
      parentNode: parentNode,
      width: "100%",
      height: "100%",
      userInfo: {
        displayName: guest ? (guestName || "Guest") : (user?.name || "Participant"),
        email: user?.email || "",
      },
      configOverwrite: {
        // Audio: start muted but show a clear unmute prompt via prejoin page
        startWithAudioMuted: true,
        startWithVideoMuted: false,
        // Pre-join page lets users test mic & camera before joining (fixes "I can't hear you" issue)
        prejoinPageEnabled: true,
        prejoinConfig: { enabled: true, hideExtraJoinButtons: ["no-audio", "by-phone"] },
        disableDeepLinking: true,
        enableWelcomePage: false,
        enableClosePage: false,
        // Allow STUN/TURN servers to reach across NAT (fixes one-way audio issues)
        disableThirdPartyRequests: false,
        requireDisplayName: false,
        enableInsecureRoomNameWarning: false,
        defaultLanguage: "en",
        // Force-fast connection: skip bandwidth probing wait
        startSilent: false,
        p2p: { enabled: true, preferH264: true, useStunTurn: true },
        // Enable noise suppression for clearer audio
        enableNoisyMicDetection: true,
        toolbarButtons: [
          "microphone", "camera", "desktop", "chat",
          "raisehand", "participants-pane", "tileview",
          "hangup", "fullscreen", "settings", "videoquality", "select-background",
        ],
      },
      interfaceConfigOverwrite: {
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        DEFAULT_BACKGROUND: "#0a2463",
        TOOLBAR_ALWAYS_VISIBLE: true,
        DEFAULT_REMOTE_DISPLAY_NAME: "Participant",
        LANG_DETECTION: false,
        MOBILE_APP_PROMO: false,
      },
    });
    apiRef.current = jitsiApi;
    setInMeeting(true);

    jitsiApi.addEventListener("readyToClose", () => {
      setInMeeting(false);
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      navigate("/meetings");
    });
    // Round 36 (#10) — auto-attendance removed; admins use the explicit
    // /api/courses/{course_id}/attendance/mark workflow instead.
    jitsiApi.addEventListener("videoConferenceJoined", () => {
      setInMeeting(true);
    });
    jitsiApi.addEventListener("videoConferenceLeft", () => {
      setInMeeting(false);
    });
  };

  useEffect(() => {
    if (!meeting || !jitsiRef.current) return;
    const script = document.createElement("script");
    script.src = "https://meet.jit.si/external_api.js";
    script.async = true;
    script.onload = () => { initJitsi(jitsiRef.current); };
    document.body.appendChild(script);

    return () => {
      if (apiRef.current) {
        try { apiRef.current.dispose(); } catch (e) {}
        apiRef.current = null;
      }
    };
  }, [meeting]);

  const openInPopup = () => {
    // Dispose current embedded Jitsi
    if (apiRef.current) {
      try { apiRef.current.dispose(); } catch (e) {}
      apiRef.current = null;
    }

    const domain = "meet.jit.si";
    const roomName = meeting.room_name;
    const displayName = encodeURIComponent(user?.name || "Participant");
    const url = `https://${domain}/${roomName}#userInfo.displayName="${displayName}"&config.prejoinPageEnabled=true&config.startWithAudioMuted=true&config.defaultLanguage="en"&config.disableThirdPartyRequests=false`;

    const popup = window.open(url, "SBLMeeting", "width=900,height=600,scrollbars=no,resizable=yes");
    popupRef.current = popup;
    setMode("popup");
    setInMeeting(true);

    // Monitor popup close
    const interval = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(interval);
        setInMeeting(false);
        setMode("embedded");
        // Re-init embedded
        if (jitsiRef.current && window.JitsiMeetExternalAPI) {
          initJitsi(jitsiRef.current);
        }
      }
    }, 1000);
  };

  const returnToEmbedded = () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    setMode("embedded");
    // Re-init embedded
    if (jitsiRef.current && window.JitsiMeetExternalAPI) {
      setTimeout(() => initJitsi(jitsiRef.current), 500);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-pulse text-white text-sm tracking-widest uppercase">Connecting to meeting...</div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-4">Meeting not found</p>
          <Link to="/meetings"><Button variant="outline" className="rounded-none text-white border-white">Back to Meetings</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-950" data-testid="meeting-room-page">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/meetings" className="text-slate-400 hover:text-white transition-colors" data-testid="back-to-meetings">
            <ArrowLeft size={20} />
          </Link>
          <div className="flex items-center gap-2">
            <VideoCamera size={18} className="text-blue-400" />
            <span className="text-white text-sm font-medium truncate max-w-xs">{meeting.title}</span>
          </div>
          {inMeeting && (
            <Badge className="bg-red-500 text-white rounded-none text-[10px] animate-pulse border-0" data-testid="live-indicator">
              LIVE
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {mode === "embedded" && inMeeting && (
            <Button onClick={openInPopup} variant="ghost" className="text-white hover:bg-slate-700 gap-2 h-8 text-xs rounded-none" data-testid="pop-out-btn">
              <ArrowsOut size={14} /> Pop Out (for screen sharing)
            </Button>
          )}
          {mode === "popup" && (
            <>
              <div className="flex items-center gap-2 text-emerald-400 text-xs">
                <VideoCamera size={14} />
                <span>Meeting running in separate window</span>
              </div>
              <Button onClick={returnToEmbedded} variant="ghost" className="text-white hover:bg-slate-700 gap-2 h-8 text-xs rounded-none" data-testid="return-embedded-btn">
                <ArrowsIn size={14} /> Return Here
              </Button>
            </>
          )}
          {mode === "embedded" && inMeeting && (
            <div className="flex items-center gap-2 text-amber-400 text-xs">
              <Warning size={14} />
              <span className="hidden sm:inline">Use "Pop Out" to share screen while navigating</span>
            </div>
          )}
        </div>
      </div>

      {/* Jitsi Container */}
      {mode === "popup" ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-white space-y-4">
            <VideoCamera size={48} className="mx-auto text-blue-400" />
            <h2 className="text-xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Meeting Running in Separate Window</h2>
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              Your meeting is running in a popup window. You can freely navigate the LMS, share your screen, and the attendees will see your shared content.
            </p>
            <p className="text-sm text-slate-400">
              Close the popup window or click "Return Here" to bring the meeting back.
            </p>
            <div className="flex gap-3 justify-center mt-4">
              <Link to="/dashboard">
                <Button variant="outline" className="rounded-none text-white border-slate-600 hover:bg-slate-800">Go to Dashboard</Button>
              </Link>
              <Link to="/courses">
                <Button variant="outline" className="rounded-none text-white border-slate-600 hover:bg-slate-800">Browse Courses</Button>
              </Link>
              <Button onClick={returnToEmbedded} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none">
                Return Meeting Here
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div ref={jitsiRef} className="flex-1" style={{ minHeight: 0 }} />
      )}
    </div>
  );
}
